import { useEffect, useState } from "react";
import { Brand } from "./Brand";

const COUNTDOWN_SECONDS = 10;

/**
 * Where /end_session lands the browser after ending the provider session.
 *
 * When the application that signed you out identified itself, the page counts
 * down and returns you there — that application then starts a fresh login,
 * which is the only way a login can begin. This provider cannot open one on its
 * own: a sign-in exists only as the answer to an authorization request, so
 * /login with no transaction is a dead end and is never linked to from here.
 *
 * The destination is resolved from the registry by client id. Nothing in the
 * URL is ever navigated to, which is what stops this page from becoming the
 * post-logout open redirect /end_session deliberately refuses.
 */
export function SignedOutPage() {
  const app = new URLSearchParams(window.location.search).get("app");
  const [target, setTarget] = useState<string | undefined>();
  const [remaining, setRemaining] = useState(COUNTDOWN_SECONDS);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (!app) return;
    const aborter = new AbortController();
    fetch(`/api/signed-out-target?client_id=${encodeURIComponent(app)}`, {
      signal: aborter.signal,
    })
      .then((response) =>
        response.ok
          ? (response.json() as Promise<{ url?: unknown }>)
          : undefined,
      )
      .then((body) => {
        // Checked rather than cast: this value decides where the browser goes.
        if (typeof body?.url === "string") setTarget(body.url);
      })
      // An unknown client is not an error worth showing. The page is still
      // correct without a way back; it just stops offering one.
      .catch(() => undefined);
    return () => aborter.abort();
  }, [app]);

  useEffect(() => {
    if (!target || paused) return;
    if (remaining <= 0) {
      window.location.replace(target);
      return;
    }
    const timer = window.setTimeout(() => setRemaining((n) => n - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [target, remaining, paused]);

  const host = target ? new URL(target).host : "";

  return (
    <main className="login-shell">
      <div className="login-card">
        <aside className="login-context" aria-label="Signed out">
          <Brand />
          <p className="login-context__blurb">
            One account for every stpwin.dev internal tool.
          </p>
        </aside>

        <section className="login-form">
          <h2>Signed out</h2>
          <p className="login-guidance">
            Your stpwin.dev Identity session has ended.
          </p>

          {target ? (
            <div className="form-stack">
              <p className="login-guidance" aria-live="polite">
                {paused
                  ? `Return to ${host} when you are ready.`
                  : `Returning to ${host} in ${remaining}s. It will start a new sign-in.`}
              </p>
              <button
                type="button"
                className="button button--primary"
                onClick={() => window.location.replace(target)}
              >
                Sign in again at {host}
              </button>
              {!paused && (
                <button
                  type="button"
                  className="button button--secondary"
                  onClick={() => setPaused(true)}
                >
                  Stay on this page
                </button>
              )}
            </div>
          ) : (
            <dl className="login-facts">
              <div>
                <dt>To sign in again</dt>
                <dd>
                  Open the protected application you want to access. It will
                  bring you back here.
                </dd>
              </div>
            </dl>
          )}

          <dl className="login-facts">
            <div>
              <dt>Still signed in elsewhere</dt>
              <dd>
                Applications keep their own sessions until those expire — this
                did not sign you out of them.
              </dd>
            </div>
          </dl>
        </section>
      </div>
    </main>
  );
}
